import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './UserPage.module.scss';
import { Button, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import {
  loadUser,
  setContextualSupport,
  switchToOrg,
  unsetContextualSupport,
  updateUserInfo,
} from 'js/actions';
import PageLayout from 'new/elements/pageLayout/PageLayout';
import WideCard from 'new/elements/WideCard/WideCard';
import SettingsFormLayout, { SettingsBlock } from '../settingsFormLayout';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import {
  GET_USER_REQUEST,
  NEX_ORG_SWITCH_REQUEST,
  UPDATE_USER_INFO_REQUEST,
} from 'js/constants/actionTypes';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { ChipBadge } from 'new/elements/ChipBadge';
import { TextField } from 'new/elements/TextField';
import { TipBox } from 'new/elements/TipBox';
import Typography from 'new/elements/Typography';
import * as PATHS from 'js/constants/pathnames';
import { getUserRoleLabel } from 'js/constants/userRoles';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { SUPPORT_KEY_ACCOUNT } from 'js/constants/supportPageKeys';
import { useImpersonatedUser } from 'new/hooks/adapters/useImpersonatedUser';
const fullNameSx = { flex: 1, marginRight: '16px' };

const UserPage = () => {
  const user = useCurrentUser();
  const currentOrg = useCurrentOrg();
  const impersonatedUser = useImpersonatedUser();
  const isEdit = useSelector((state: any) => state.users.isEdit);
  const dispatch = useDispatch<any>();
  const loading = useIsLoading(UPDATE_USER_INFO_REQUEST, GET_USER_REQUEST, NEX_ORG_SWITCH_REQUEST);

  useEffect(() => {
    dispatch(setContextualSupport(SUPPORT_KEY_ACCOUNT));
    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_ACCOUNT));
    };
  }, []);
  useEffect(() => {
    setValue('full_name', user.full_name);
  }, [user.full_name]);

  useEffect(() => {
    dispatch(loadUser(user.id));
  }, []);

  const handleSwitchToOrg = useCallback((id) => {
    return () => {
      dispatch(switchToOrg(id));
    };
  }, []);

  const handleSetAsDefault = useCallback(
    (id) => {
      return () => {
        dispatch(
          updateUserInfo(user.id, {
            default_org_id: id,
          }),
        );
      };
    },
    [user],
  );

  const onSubmit = useCallback(
    (data) => {
      dispatch(updateUserInfo(user.id, data)).then(() => {
        reset(data);
      });
    },
    [user],
  );

  const {
    register,
    setValue,
    reset,
    formState: { errors, isDirty },
    handleSubmit,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      full_name: user.full_name || '',
    },
  });

  return (
    <PageLayout
      title="User Information"
      loading={loading}
      description="Update your name, email, and org membership preferences."
    >
      <SettingsFormLayout>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SettingsBlock label="Name">
            <Stack direction="row" alignItems="baseline">
              <TextField
                sx={fullNameSx}
                type="text"
                label="Full Name"
                error={!!errors.full_name}
                helperText={errors.full_name ? <>{errors.full_name?.message}</> : undefined}
                placeholder="Please, input full name"
                {...register('full_name', {
                  required: 'Please, input the user name',
                  minLength: 2,
                  maxLength: 100,
                })}
                disabled={isEdit}
              />
              <Button
                variant="contained"
                size="large"
                color="primary"
                type="submit"
                disabled={isEdit || Object.keys(errors).length > 0 || !isDirty}
              >
                Save
              </Button>
            </Stack>
          </SettingsBlock>
          <SettingsBlock label="Email address">
            <Stack className={styles.emailContainer}>
              <TextField label="Email" value={user.email} type="text" readOnly disabled />
              <TipBox className={styles.tipBox}>
                <Typography variant="body2">
                  To change your email address, please send an email to{' '}
                  <a href="mailto:support@nexla.com">support@nexla.com</a>.
                </Typography>
              </TipBox>
            </Stack>
          </SettingsBlock>

          <SettingsBlock
            label="Org membership"
            description="Organizations associated with your email address."
          >
            <Stack spacing="16px">
              {(user.org_memberships || []).map((org) => {
                const actions = [];
                if (currentOrg.id !== org.id) {
                  actions.push({
                    label: 'Switch active Org',
                    onClick: handleSwitchToOrg(org.id),
                  });
                }
                if (user.default_org !== org.id) {
                  actions.push({
                    label: 'Set default Org',
                    onClick: handleSetAsDefault(org.id),
                  });
                }
                const menuActions = impersonatedUser ? [] : actions;
                return (
                  <WideCard
                    key={org.id}
                    name={org.name}
                    description={getUserRoleLabel(org.access_role)}
                    current={currentOrg.id === org.id}
                    status={org.cluster_status?.toLowerCase()}
                    menuActions={menuActions}
                    rightSlot={
                      <Stack direction="row" alignItems="center" gap="8px">
                        {currentOrg.id === org.id ? (
                          <ChipBadge label="Active" color="primary" />
                        ) : null}
                        {user.default_org === org.id ? (
                          <ChipBadge label="Default" color="disabled" />
                        ) : null}
                      </Stack>
                    }
                  />
                );
              })}
            </Stack>
          </SettingsBlock>
        </form>
      </SettingsFormLayout>
    </PageLayout>
  );
};

UserPage.screenConfig = {
  path: `${PATHS.ROUTE_REDESIGN}/settings/user`,
  screenName: 'settings.User',
  header: {
    title: 'User Information',
    slugTitle: 'User Information',
  },
  domain: 'settings',
};

export default UserPage;
